import { z } from 'zod';

import { definedNotNull } from '../defined/index.js';

declare const voucherIdBrand: unique symbol;

/** Branded voucher id; wire form is `"series-number"` (Rust `SerializeToString`). */
export type VoucherId = string & { readonly [voucherIdBrand]: 'VoucherId' };

/** The single branding cast for the module. */
const brand = (value: string): VoucherId => value as VoucherId;

/** Maps a voucher id to its display parts (the audit `VoucherIdMap` shape). */
export type VoucherDisplayMap = Map<VoucherId, { series: string; number: string }>;

export const VoucherId = {
  /** Trusted-boundary cast (WASM output, already-validated strings). */
  unsafe(value: string): VoucherId {
    return brand(value);
  },

  /**
   * Human-readable `"series-number"` rendering, resolved through the display map;
   * falls back to the raw id when the map has no entry for it.
   */
  display(value: VoucherId, map: VoucherDisplayMap): string {
    const parts = map.get(value);
    return definedNotNull(parts) ? `${parts.series}-${parts.number}` : value;
  },

  schema: z.string().transform(brand),
};

/** Co-located schema; kept as a named export for the many existing importers. */
export const voucherIdSchema = VoucherId.schema;
